import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setError(t('resetPassword.requestNewLink'));
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setUserEmail(response.data.email);
    } catch (err) {
      setError(err.response?.data?.detail || t('resetPassword.invalidOrExpired'));
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('resetPassword.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('resetPassword.errorResetting'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#1D262D] flex items-center justify-center p-4" data-testid="reset-password-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
          <p className="text-slate-400">{t('resetPassword.verifyingLink')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1D262D] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="SGFI Capital" className="h-14 w-auto object-contain mx-auto mb-6" />
        </div>

        <div className="bg-[#252F38] border border-white/10 rounded-2xl p-8 shadow-xl">
          {success ? (
            <div className="text-center space-y-6" data-testid="reset-success">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('resetPassword.passwordChanged')}</h2>
                <p className="text-slate-400">
                  {t('resetPassword.passwordChangedSuccess')}
                </p>
              </div>
              <Button
                onClick={() => navigate('/')}
                className="w-full h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg"
              >
                {t('resetPassword.goToLogin')}
              </Button>
            </div>
          ) : !tokenValid ? (
            <div className="text-center space-y-6" data-testid="reset-invalid">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('resetPassword.invalidLink')}</h2>
                <p className="text-slate-400">{error}</p>
              </div>
              <Button
                onClick={() => navigate('/')}
                className="w-full h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('resetPassword.backToLogin')}
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">{t('resetPassword.newPassword')}</h2>
                <p className="text-slate-400 text-sm">
                  {t('resetPassword.setNewPasswordFor')} <span className="text-blue-400">{userEmail}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('resetPassword.newPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="pl-12 pr-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('resetPassword.confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="pl-12 pr-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="text-xs text-slate-500 space-y-1">
                  <p>{t('resetPassword.passwordRequirements')}</p>
                  <ul className="list-disc list-inside">
                    <li className={password.length >= 6 ? 'text-emerald-400' : ''}>
                      {t('resetPassword.atLeast6Chars')}
                    </li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('resetPassword.changePassword')
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full text-center text-slate-400 hover:text-slate-300 text-sm flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('resetPassword.backToLogin')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
